import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  Button,
  Card,
  Input,
  Label,
  apiClient,
  useToast,
} from "@toss-ads/ui";

const schema = z.object({
  email: z
    .string()
    .min(1, "이메일을 입력해주세요")
    .email("올바른 이메일 형식이 아닙니다")
    .max(320),
  role: z.enum(["user", "admin", "developer"]),
  note: z.string().max(500, "메모는 500자 이내").optional(),
  phone: z.string().max(32, "전화번호는 32자 이내").optional(),
  smsOnLead: z.boolean().optional(),
});

type FormValues = z.infer<typeof schema>;

interface CreateUserResponse {
  id: string;
  email: string;
  role: "user" | "admin" | "developer";
}

export function UserCreatePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { role: "user" },
  });

  const createUser = useMutation({
    mutationFn: (dto: FormValues) =>
      apiClient<CreateUserResponse>("/api/admin/users", {
        method: "POST",
        body: {
          email: dto.email,
          role: dto.role,
          note: dto.note?.trim() ? dto.note.trim() : undefined,
          phone: dto.phone?.trim() ? dto.phone.trim() : undefined,
          smsOnLead: dto.smsOnLead ?? undefined,
        },
      }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success("유저가 생성되었습니다 — 프로젝트 권한을 매핑해주세요");
      navigate(`/users/${created.id}`);
    },
    onError: (e) => {
      if (e instanceof ApiError) {
        if (e.status === 409) {
          setError("email", { message: "이미 존재하는 이메일입니다" });
        } else if (e.status === 400) {
          toast.error("입력 정보를 확인해주세요");
        } else {
          toast.error(`생성 실패 — ${e.message}`);
        }
      } else {
        toast.error("네트워크 오류");
      }
    },
  });

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Button variant="text" size="md" onClick={() => navigate("/users")}>
          ← 유저 목록
        </Button>
      </div>

      <Card className="max-w-xl mx-auto p-7">
        <h2 className="text-[18px] font-semibold tracking-tight mb-1.5">
          신규 유저 생성
        </h2>
        <p className="text-[13px] text-ink-muted mb-6 leading-relaxed">
          유저 이메일과 권한을 지정하세요.
          <br />
          생성된 유저는 이 이메일로 인증코드를 받아 로그인합니다.
          <br />
          프로젝트 권한은 생성 후 유저 상세에서 매핑합니다.
        </p>

        <form onSubmit={handleSubmit((v) => createUser.mutate(v))}>
          <div className="mb-4">
            <Label htmlFor="create-email">이메일</Label>
            <Input
              id="create-email"
              type="email"
              autoComplete="off"
              placeholder="user@example.com"
              error={!!errors.email}
              {...register("email")}
            />
            {errors.email && (
              <p className="text-[11px] text-danger mt-1">
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="mb-4">
            <Label htmlFor="create-role">권한</Label>
            <select
              id="create-role"
              {...register("role")}
              className="block w-full h-[42px] px-3 text-[13px] rounded-base border border-border-strong bg-surface text-ink transition-colors focus:outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgb(43_111_216/0.08)]"
            >
              <option value="user">user (광고주 담당자)</option>
              <option value="admin">admin (관리자)</option>
              <option value="developer">developer (운영자)</option>
            </select>
          </div>

          <div className="mb-6">
            <Label htmlFor="create-note">메모</Label>
            <Input
              id="create-note"
              autoComplete="off"
              placeholder="예: 법무법인해랑 / HR팀 김담당"
              error={!!errors.note}
              {...register("note")}
            />
            <p className="text-[11px] text-ink-subtle mt-1">
              어드민 식별용. 매핑된 광고주나 담당자 이름 등을 자유롭게.
            </p>
            {errors.note && (
              <p className="text-[11px] text-danger mt-1">
                {errors.note.message}
              </p>
            )}
          </div>

          <div className="mb-4">
            <Label htmlFor="create-phone">SMS 수신번호 (선택)</Label>
            <Input
              id="create-phone"
              type="tel"
              autoComplete="off"
              placeholder="01012345678"
              error={!!errors.phone}
              {...register("phone")}
            />
            {errors.phone && (
              <p className="text-[11px] text-danger mt-1">
                {errors.phone.message}
              </p>
            )}
          </div>

          <label className="flex items-center gap-2 mb-6 text-[13px] text-ink cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4"
              {...register("smsOnLead")}
            />
            담당 광고주에 새 리드가 오면 SMS 수신
          </label>

          <p className="text-[11px] text-ink-subtle mb-4">
            담당자 라벨 색은 생성 시 자동 배정됩니다. (수정은 상세에서)
          </p>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button
              type="button"
              variant="text"
              size="md"
              onClick={() => navigate("/users")}
            >
              취소
            </Button>
            <Button
              type="submit"
              size="md"
              loading={createUser.isPending}
            >
              생성
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
